import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Anchor, Loader, SimpleGrid, Tabs, Text } from '@mantine/core';
import { type DatesRangeValue } from '@mantine/dates';
import { FinanceDateRangeInput } from '@repo/date-range-picker';
import {
  IoCalendarOutline,
  IoCashOutline,
  IoRemoveCircleOutline,
  IoTrendingUpOutline,
  IoWalletOutline,
} from 'react-icons/io5';
import { OptionsTable, type OptionsTableColumn } from '@components';
import {
  sellerFinanceApi,
  type SellerLedgerEntryRow,
} from '@modules/seller-finance/api/seller-finance.api';
import { PillGroup, RefetchButton } from '@shared/ui';
import { formatPrice } from '@shared/utils';
import { SellerAdvertisingSection } from './SellerAdvertisingSection';
import './SellerFinanceTab.css';

function utcDateLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('ru-RU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function localCalendarYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type PeriodPreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

function localRangeForPreset(
  preset: Exclude<PeriodPreset, 'custom'>,
): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const to = localCalendarYmd(now);
  if (preset === 'today') return { dateFrom: to, dateTo: to };

  const fromDate = new Date(now);
  if (preset === 'yesterday') {
    fromDate.setDate(fromDate.getDate() - 1);
    const day = localCalendarYmd(fromDate);
    return { dateFrom: day, dateTo: day };
  }
  if (preset === 'week') {
    fromDate.setDate(fromDate.getDate() - 6);
    return { dateFrom: localCalendarYmd(fromDate), dateTo: to };
  }
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return { dateFrom: localCalendarYmd(monthStart), dateTo: to };
}

const PRESET_LABELS: { id: PeriodPreset; label: string }[] = [
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
  { id: 'custom', label: 'Свой период' },
];

function ledgerKindLabel(kind: string): string {
  switch (kind) {
    case 'ORDER_ITEMS_REVENUE':
      return 'Выручка по заказу';
    case 'PICKUP_ON_RECEIPT_REVENUE':
      return 'Самовывоз (оплата при получении)';
    case 'PLATFORM_COMMISSION':
      return 'Комиссия платформы';
    case 'PROMOTION_DEDUCTION':
      return 'Промо и скидки';
    case 'DELIVERY_ADJUSTMENT':
      return 'Доставка';
    case 'ADVERTISING_DEDUCTION':
      return 'Продвижение / реклама';
    case 'SELLER_ORDER_CANCELLATION_PENALTY':
      return 'Штраф за отмену / возврат';
    case 'WEEKLY_PAYOUT_TRANSFER':
      return 'Выплата на счёт';
    case 'ORDER_ACCRUAL_STORNO':
      return 'Отмена начислений по заказу';
    case 'MANUAL_ADJUSTMENT':
      return 'Корректировка баланса';
    case 'ADVERTISING_WALLET_TRANSFER':
      return 'Перевод на рекламный баланс';
    default:
      return kind;
  }
}

function MetricCard(props: {
  title: string;
  hint: string;
  value: string;
  loading?: boolean;
  tone?: 'accent' | 'blue' | 'orange' | 'green';
  icon?: ReactNode;
}) {
  const tone = props.tone ?? 'accent';
  return (
    <div className={`seller-dashboard-metric seller-dashboard-metric--${tone}`}>
      <div className="seller-dashboard-metric__top">
        <Text className="seller-dashboard-metric__title">{props.title}</Text>
        <span className="seller-dashboard-metric__dot" aria-hidden>
          {props.icon}
        </span>
      </div>
      <Text className="seller-dashboard-metric__value">
        {props.loading ? '…' : props.value}
      </Text>
      <Text className="seller-dashboard-metric__hint">{props.hint}</Text>
    </div>
  );
}

type SellerFinanceTabProps = {
  sellerId: number;
  shopName?: string | null;
  payoutsSection: ReactNode;
};

export function SellerFinanceTab({ sellerId, shopName, payoutsSection }: SellerFinanceTabProps) {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<PeriodPreset>('week');
  const [customRange, setCustomRange] = useState<DatesRangeValue>([null, null]);

  const ledgerQuery = useQuery({
    queryKey: ['seller-finance', 'seller-ledger', sellerId],
    queryFn: () => sellerFinanceApi.getSellerLedger(sellerId),
  });

  const ledgerColumns: OptionsTableColumn<SellerLedgerEntryRow>[] = useMemo(
    () => [
      {
        key: 'occurredAt',
        label: 'Дата',
        render: (row) => new Date(row.occurredAt).toLocaleString('ru-RU'),
      },
      {
        key: 'operation',
        label: 'Операция',
        render: (row) => {
          const label = ledgerKindLabel(row.kind);
          const title = row.title?.trim();
          return <span className="seller-finance-op-label">{title || label}</span>;
        },
      },
      {
        key: 'order',
        label: 'Заказ',
        render: (row) =>
          row.orderId != null ? (
            <Anchor
              component={Link}
              to={`/orders/${row.orderId}`}
              state={{ from: 'seller', sellerId }}
              size="sm"
            >
              {`${row.orderNumber ?? row.orderId}`}
            </Anchor>
          ) : (
            <span className="seller-finance-muted">—</span>
          ),
      },
      {
        key: 'amountRub',
        label: 'Сумма',
        render: (row) => (
          <span
            className={
              row.amountRub >= 0
                ? 'seller-finance-amount seller-finance-amount--plus'
                : 'seller-finance-amount seller-finance-amount--minus'
            }
          >
            {row.amountRub >= 0 ? '+' : ''}
            {formatPrice(row.amountRub)}
          </span>
        ),
      },
    ],
    [sellerId],
  );

  const refreshFinance = () =>
    queryClient.invalidateQueries({ queryKey: ['seller-finance', 'seller-ledger', sellerId] });

  const ledgerRows = ledgerQuery.data?.entries ?? [];
  const currentBalanceRub = ledgerQuery.data?.currentBalanceRub ?? 0;
  const week = ledgerQuery.data?.openWeekAccruals;
  const planned = ledgerQuery.data?.plannedPayout;

  const ledgerLoading = ledgerQuery.isPending || ledgerQuery.isFetching;

  const payoutScheduleText = planned
    ? new Date(planned.scheduledAt).toLocaleString('ru-RU', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    : '—';

  const plannedHint = planned
    ? `за ${utcDateLabel(planned.salesDateFrom)} — ${utcDateLabel(planned.salesDateTo)}${planned.alreadyPaid ? ' · уже проведена' : ''
    }`
    : week
      ? 'ближайший прогон по расписанию'
      : 'дата ближайшей выплаты';

  const { dateFrom, dateTo } = useMemo(() => {
    if (preset === 'custom') {
      const [a, b] = customRange;
      if (!a || !b) return localRangeForPreset('week');
      const from = typeof a === 'string' ? a : localCalendarYmd(a);
      const to = typeof b === 'string' ? b : localCalendarYmd(b);
      return from <= to ? { dateFrom: from, dateTo: to } : { dateFrom: to, dateTo: from };
    }
    return localRangeForPreset(preset);
  }, [preset, customRange]);

  const filteredLedgerRows = useMemo(
    () =>
      ledgerRows.filter((row) => {
        const d = localCalendarYmd(new Date(row.occurredAt));
        return d >= dateFrom && d <= dateTo;
      }),
    [ledgerRows, dateFrom, dateTo],
  );

  const periodTotals = useMemo(() => {
    const isPickupOnReceiptStorno = (row: {
      kind: string;
      meta: unknown;
      amountRub: number;
    }) =>
      row.kind === 'ORDER_ACCRUAL_STORNO' &&
      !!row.meta &&
      typeof row.meta === 'object' &&
      (row.meta as { pickupOnReceipt?: unknown }).pickupOnReceipt === true;

    const byKind = filteredLedgerRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.kind] = (acc[row.kind] ?? 0) + row.amountRub;
      return acc;
    }, {});

    const deliveryRows = filteredLedgerRows.filter((row) => row.kind === 'DELIVERY_ADJUSTMENT');
    const deliveryCompensationToRevenue = deliveryRows.reduce(
      (acc, row) => acc + (row.amountRub > 0 ? row.amountRub : 0),
      0,
    );
    const deliveryDeduction = deliveryRows.reduce(
      (acc, row) => acc + (row.amountRub < 0 ? -row.amountRub : 0),
      0,
    );
    const pickupStorno = filteredLedgerRows
      .filter(isPickupOnReceiptStorno)
      .reduce((acc, row) => acc + row.amountRub, 0);
    const onlineStorno = filteredLedgerRows
      .filter((row) => row.kind === 'ORDER_ACCRUAL_STORNO' && !isPickupOnReceiptStorno(row))
      .reduce((acc, row) => acc + row.amountRub, 0);

    // Онлайн-возврат гасит выручку в карточке (как сторно самовывоза).
    const onlineRevenue =
      Math.round(
        ((byKind.ORDER_ITEMS_REVENUE ?? 0) +
          deliveryCompensationToRevenue +
          onlineStorno) *
        100,
      ) / 100;
    const onReceiptRevenue =
      Math.round(((byKind.PICKUP_ON_RECEIPT_REVENUE ?? 0) + pickupStorno) * 100) / 100;
    const grossRevenue = Math.round((onlineRevenue + onReceiptRevenue) * 100) / 100;
    const commission = Math.max(0, -(byKind.PLATFORM_COMMISSION ?? 0));
    const promotion = Math.max(
      0,
      -((byKind.PROMOTION_DEDUCTION ?? 0) + (byKind.ADVERTISING_DEDUCTION ?? 0)),
    );
    const penalties = Math.max(0, -(byKind.SELLER_ORDER_CANCELLATION_PENALTY ?? 0));
    const platformDeduction = commission + promotion + penalties + deliveryDeduction;
    const periodNet =
      Math.round((grossRevenue - platformDeduction) * 100) / 100;
    const totalByAllTransactions = filteredLedgerRows.reduce(
      (acc, row) => acc + row.amountRub,
      0,
    );

    return {
      grossRevenue,
      onlineRevenue,
      onReceiptRevenue,
      commission,
      deliveryDeduction,
      promotion,
      penalties,
      platformDeduction,
      periodNet,
      totalByAllTransactions,
    };
  }, [filteredLedgerRows]);

  const deductionHint =
    [
      periodTotals.commission > 0
        ? `комиссия и эквайринг ${formatPrice(periodTotals.commission)}`
        : null,
      periodTotals.deliveryDeduction > 0
        ? `доставка ${formatPrice(periodTotals.deliveryDeduction)}`
        : null,
      periodTotals.promotion > 0 ? `продвижение ${formatPrice(periodTotals.promotion)}` : null,
      periodTotals.penalties > 0 ? `штрафы ${formatPrice(periodTotals.penalties)}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'комиссия, доставка, продвижение, штрафы';

  return (
    <div className="seller-finance-tab">
      <Tabs defaultValue="operations" keepMounted={false} className="seller-finance-tabs">
        <Tabs.List className="seller-finance-tabs-list">
          <Tabs.Tab value="operations" className="seller-finance-tabs-tab">
            Операции
          </Tabs.Tab>
          <Tabs.Tab value="payouts" className="seller-finance-tabs-tab">
            Выплаты
          </Tabs.Tab>
          <Tabs.Tab value="advertising" className="seller-finance-tabs-tab">
            Реклама
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="operations" className="seller-finance-tabs-panel">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <MetricCard
              title="К выплате"
              hint="Невыплаченный остаток (без самовывоза при получении)"
              value={formatPrice(currentBalanceRub)}
              loading={ledgerLoading}
              tone="accent"
              icon={<IoWalletOutline />}
            />
            <MetricCard
              title="Плановая выплата"
              hint={`${payoutScheduleText} · ${plannedHint}`}
              value={formatPrice(planned?.netAmountRub ?? 0)}
              loading={ledgerLoading}
              tone="green"
              icon={<IoCalendarOutline />}
            />
          </SimpleGrid>

          <div className="seller-dashboard__period seller-finance-period">
            <Text className="seller-dashboard__period-label">Период</Text>
            <PillGroup
              ariaLabel="Период"
              options={PRESET_LABELS.map((p) => ({ value: p.id, label: p.label }))}
              value={preset}
              onChange={setPreset}
            />
            {preset === 'custom' ? (
              <div className="seller-dashboard__period-dates">
                <FinanceDateRangeInput
                  value={customRange}
                  onChange={setCustomRange}
                  valueFormat="DD.MM.YYYY"
                  placeholder="Выберите даты"
                  size="sm"
                  radius="md"
                />
              </div>
            ) : null}
          </div>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <MetricCard
              title="Выручка"
              hint={`онлайн ${formatPrice(periodTotals.onlineRevenue)} · при получении ${formatPrice(periodTotals.onReceiptRevenue)}`}
              value={formatPrice(periodTotals.grossRevenue)}
              loading={ledgerLoading}
              tone="accent"
              icon={<IoCashOutline />}
            />
            <MetricCard
              title="Удержание"
              hint={deductionHint}
              value={`−${formatPrice(periodTotals.platformDeduction)}`}
              loading={ledgerLoading}
              tone="orange"
              icon={<IoRemoveCircleOutline />}
            />
            <MetricCard
              title="Итого за период"
              hint="выручка минус удержания"
              value={formatPrice(periodTotals.periodNet)}
              loading={ledgerLoading}
              tone="blue"
              icon={<IoTrendingUpOutline />}
            />
          </SimpleGrid>

          <section className="seller-dashboard-card seller-finance-card">
            <div className="seller-dashboard-card__head">
              <h2 className="seller-dashboard-card__title">История операций</h2>
            </div>
            <div className="seller-finance-table-wrap seller-finance-desktop-table">
              <OptionsTable<SellerLedgerEntryRow>
                columns={ledgerColumns}
                data={filteredLedgerRows}
                loading={ledgerQuery.isLoading}
                error={ledgerQuery.error}
                refetch={() => refreshFinance()}
                emptyMessage="Нет операций за выбранный период."
              />
            </div>
            <div className="seller-finance-mobile-list seller-finance-mobile-list--ops" aria-label="История операций">
              {ledgerQuery.error ? (
                <RefetchButton
                  refetch={() => refreshFinance()}
                  isLoading={ledgerQuery.isLoading || ledgerQuery.isFetching}
                  errorMessage={
                    ledgerQuery.error instanceof Error
                      ? ledgerQuery.error.message
                      : 'Не удалось загрузить операции'
                  }
                />
              ) : ledgerQuery.isLoading ? (
                <div className="seller-finance-mobile-empty">
                  <Loader size="sm" />
                </div>
              ) : filteredLedgerRows.length === 0 ? (
                <div className="seller-finance-mobile-empty" role="status">
                  Нет операций за выбранный период.
                </div>
              ) : (
                filteredLedgerRows.map((row) => {
                  const opLabel = row.title?.trim() || ledgerKindLabel(row.kind);
                  return (
                    <div key={row.id} className="seller-finance-mobile-card">
                      <div className="seller-finance-mobile-card__top">
                        <span className="seller-finance-op-label">{opLabel}</span>
                        <span
                          className={
                            row.amountRub >= 0
                              ? 'seller-finance-amount seller-finance-amount--plus'
                              : 'seller-finance-amount seller-finance-amount--minus'
                          }
                        >
                          {row.amountRub >= 0 ? '+' : ''}
                          {formatPrice(row.amountRub)}
                        </span>
                      </div>
                      <div className="seller-finance-mobile-card__meta">
                        {new Date(row.occurredAt).toLocaleString('ru-RU')}
                      </div>
                      <div className="seller-finance-mobile-card__row">
                        <span className="seller-finance-mobile-card__label">Заказ</span>
                        <span className="seller-finance-mobile-card__value">
                          {row.orderId == null ? (
                            <span className="seller-finance-muted">—</span>
                          ) : (
                            <Anchor
                              component={Link}
                              to={`/orders/${row.orderId}`}
                              state={{ from: 'seller', sellerId }}
                              size="sm"
                            >
                              {`${row.orderNumber ?? row.orderId}`}
                            </Anchor>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </Tabs.Panel>

        <Tabs.Panel value="payouts" className="seller-finance-tabs-panel">
          {payoutsSection}
        </Tabs.Panel>
        <Tabs.Panel value="advertising" className="seller-finance-tabs-panel">
          <SellerAdvertisingSection sellerId={sellerId} shopName={shopName} />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
